import { createTheme, alpha } from '@mui/material/styles';

const theme = createTheme({
  spacing: 8,
  breakpoints: {
    values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 },
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#1F2F42',
      light: '#3A5068',
      dark: '#141F2E',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#C89E57',
      light: '#D9B97A',
      dark: '#A67F3D',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F5F6F4',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1F2F42',
      secondary: '#5A6A7A',
    },
    error: { main: '#C0392B' },
    warning: { main: '#D4A017' },
    success: { main: '#2E7D5B' },
    info: { main: '#3B82F6' },
    grey: {
      50: '#F8F9F7', 100: '#F0F1EF', 200: '#DFE1DD', 300: '#C5C8C3',
      400: '#8E9490', 500: '#5A6A7A', 600: '#475569', 700: '#334155',
      800: '#1E293B', 900: '#1F2F42',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '2.5rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 },
    h2: { fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3 },
    h3: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.3 },
    h4: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: '0.938rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.6 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
  },
  shape: { borderRadius: 12 },
  shadows: [
    'none',
    '0 1px 2px rgba(0,0,0,0.04)',
    '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
    '0 4px 6px -1px rgba(0,0,0,0.06), 0 2px 4px -1px rgba(0,0,0,0.04)',
    '0 10px 15px -3px rgba(0,0,0,0.06), 0 4px 6px -2px rgba(0,0,0,0.03)',
    '0 20px 25px -5px rgba(0,0,0,0.06), 0 10px 10px -5px rgba(0,0,0,0.02)',
    '0 25px 50px -12px rgba(0,0,0,0.15)',
    ...Array(18).fill('0 25px 50px -12px rgba(0,0,0,0.15)'),
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 20px',
          fontWeight: 600,
          fontSize: '0.875rem',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': { transform: 'translateY(-1px)' },
          '&:active': { transform: 'translateY(0)' },
        },
        contained: ({ theme }) => ({
          boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.25)}`,
          '&:hover': {
            boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.35)}`,
          },
        }),
        outlined: ({ theme }) => ({
          borderWidth: '1.5px',
          '&:hover': { borderWidth: '1.5px', background: alpha(theme.palette.primary.main, 0.04) },
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 16 },
        elevation1: { boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            transition: 'all 0.2s ease',
            '& fieldset': { borderColor: 'rgba(0,0,0,0.1)', borderWidth: '1.5px' },
            '&:hover fieldset': { borderColor: '#1F2F42' },
            '&.Mui-focused fieldset': {
              borderColor: '#1F2F42',
              boxShadow: `0 0 0 3px ${alpha('#1F2F42', 0.12)}`,
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          boxShadow: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 500 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12 },
      },
    },
  },
});

export default theme;
